import { useRef, useState } from 'react'
import styled from 'styled-components'
import useTranslation from '../../hooks/useTranslation'
import useApi from '../../hooks/useApi'

const DropZone = styled.div`
  border: 2px dashed ${({ theme, dragging }) =>
    dragging ? (theme.color?.primary || '#2563eb') : (theme.border?.default || '#d1d5db')};
  border-radius: 8px;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  background: ${({ theme, dragging }) =>
    dragging ? (theme.dropdown?.selectedBackground || '#eff6ff') : 'transparent'};
  transition: border-color 0.15s, background 0.15s;
  color: ${({ theme }) => theme.font?.label || '#6b7280'};
  font-size: 0.875rem;
`

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.border?.default || '#d1d5db'};
  border-radius: 6px;
  font-size: 0.875rem;
`

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.font?.subtle || '#6b7280'};
  font-size: 1.1rem;
  padding: 0;
  line-height: 1;
  &:hover { color: ${({ theme }) => theme.color?.danger || '#dc2626'}; }
`

const ErrorMsg = styled.span`
  color: ${({ theme }) => theme.color?.danger || '#dc2626'};
  font-size: 0.8125rem;
`

const formatSize = (bytes) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const FileUpload = ({ name, fileNameField, fileSizeField, folder, accept, label, onChange, value, fileName }) => {
  const { t } = useTranslation()
  const { postMeForm } = useApi()
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const upload = async (file) => {
    setUploading(true)
    setError(null)
    try {
      const dataItems = { folder: folder || 'documents' }
      const response = await postMeForm('file', file, dataItems)
      const record = response?.data?.record
      if (!record) throw new Error('Upload failed')
      onChange({
        [name]: record.id,
        ...(fileNameField ? { [fileNameField]: record.fileName || file.name } : {}),
        ...(fileSizeField ? { [fileSizeField]: file.size } : {})
      })
    } catch (e) {
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleFiles = (files) => {
    if (files?.[0]) upload(files[0])
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleRemove = () => {
    onChange({
      [name]: null,
      ...(fileNameField ? { [fileNameField]: null } : {}),
      ...(fileSizeField ? { [fileSizeField]: null } : {})
    })
  }

  if (value) {
    return (
      <FileRow>
        <span>{fileName || value}</span>
        <RemoveBtn type="button" onClick={handleRemove}>×</RemoveBtn>
      </FileRow>
    )
  }

  return (
    <>
      <DropZone
        dragging={dragging ? 1 : 0}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {uploading ? 'Uploading...' : t(label) || 'Click or drag a file here'}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />
      </DropZone>
      {error && <ErrorMsg>{error}</ErrorMsg>}
    </>
  )
}

export default FileUpload
